import type { TOC } from '@ember/component/template-only';
import Text from './text';
import type { ComponentLike } from '@glint/template';
import type { ReimaginedColor } from 'tio-ui/color-tokens';

/**
 * Color theme options for the stat card
 */
export type StatCardColor = ReimaginedColor;

/**
 * StatCard - A metric display card for dashboards and summary sections.
 *
 * Composes against:
 *   - `.app-stat-card` for the surface (bg / border / padding)
 *   - `.app-icon-badge` for the colored icon wrapper in the default layout
 *
 * Two layouts via `@compact`:
 *   - **Default (false):** Icon wrapped in colored badge, p-5 padding
 *   - **Compact (true):** Inline icon (no bg), p-4 padding — for dense grids
 *
 * @example Default layout
 * ```gts
 * <StatCard
 *   @icon={{PhCurrencyDollar}}
 *   @label="Annual Benefit"
 *   @value="$5,250"
 *   @subtext="Tax-free under Section 127"
 *   @color="emerald"
 * />
 * ```
 *
 * @example Compact layout
 * ```gts
 * <StatCard
 *   @compact={{true}}
 *   @icon={{PhUsers}}
 *   @label="In Pipeline"
 *   @value={{85}}
 *   @valueColor="violet"
 *   @trend="up"
 *   @trendText="+12% YoY"
 * />
 * ```
 */
interface StatCardSignature {
  Args: {
    /** Phosphor icon component to display (optional in compact mode) */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Label text displayed above the value */
    label: string;
    /** Main value to display */
    value: string | number;
    /** Secondary text below the value */
    subtext?: string;
    /** Additional secondary line (compact layouts). Rendered only when @trendText is not provided. */
    secondaryText?: string;
    /** Optional suffix appended to the value (e.g., "%", "mo") */
    suffix?: string;
    /** Color theme for icon badge (default layout) */
    color?: StatCardColor;
    /** Color theme for the value text (overrides default --text-primary) */
    valueColor?: StatCardColor;
    /** Color theme for the icon in compact mode (overrides @color) */
    iconColor?: StatCardColor;
    /** Override class for value styling (e.g. for colored values via app-text-* recipe) */
    valueClass?: string;
    /** Trend direction — colors the trend text */
    trend?: 'up' | 'down' | 'stable';
    /** Trend label text displayed below the value */
    trendText?: string;
    /** Compact layout: smaller padding, inline icon (no bg circle) */
    compact?: boolean;
  };
  Blocks: {
    /** Optional extra content below the value (e.g., progress bar) */
    extra?: [];
  };
  Element: HTMLDivElement;
}

/**
 * Map decorative color → semantic text-color utility (TIO recipe).
 * Local to StatCard — collapses 15 decorative names onto 6 canonical
 * recipes, matching the .app-icon-badge / .app-highlight-card collapse.
 * Kept inline (not a shared helper) because the mapping is only useful
 * to StatCard's value + compact-mode-icon color slots.
 */
function recipeFor(color?: StatCardColor): string {
  if (!color || color === 'gray') return 'text-[var(--text-tertiary)]';
  if (color === 'emerald' || color === 'green') return 'text-[var(--success-icon)]';
  if (
    color === 'teal' ||
    color === 'cyan' ||
    color === 'sky' ||
    color === 'blue' ||
    color === 'indigo'
  )
    return 'text-[var(--info-icon)]';
  if (color === 'violet' || color === 'purple') return 'text-[var(--brand-accent)]';
  if (color === 'pink' || color === 'rose' || color === 'red') return 'text-[var(--danger-icon)]';
  if (color === 'amber' || color === 'orange') return 'text-[var(--warning-icon)]';
  return 'text-[var(--text-tertiary)]';
}

const valueColorClass = (valueColor?: StatCardColor, valueClass?: string) => {
  if (valueClass) return valueClass;
  if (valueColor) return recipeFor(valueColor);
  return ''; // Falls through to .app-stat-card-value default (--text-primary)
};

const iconColorClass = (iconColor?: StatCardColor, color?: StatCardColor) =>
  recipeFor(iconColor ?? color);

const trendColorClass = (trend?: 'up' | 'down' | 'stable') => {
  if (trend === 'down') return 'tio-text-error';
  if (trend === 'stable') return 'text-[var(--text-tertiary)]';
  return 'tio-text-success';
};

const StatCard: TOC<StatCardSignature> = <template>
  <div
    data-test-stat-card
    class="app-stat-card {{if @compact 'app-stat-card--compact'}}"
    role="group"
    aria-label={{@label}}
    ...attributes
  >
    {{#if @compact}}
      {{! Compact layout: inline icon (no bg badge) }}
      {{#if @icon}}
        <div class="tio-row-tight mb-2 min-w-0">
          {{#let @icon as |Icon|}}
            <Icon @size={{18}} class={{iconColorClass @iconColor @color}} />
          {{/let}}
          <Text
            data-test-stat-card-label
            @variant="muted"
            @size="sm"
            class="app-stat-card-label line-clamp-2 leading-snug min-w-0"
          >{{@label}}</Text>
        </div>
      {{else}}
        <Text
          data-test-stat-card-label
          class="app-stat-card-label mb-1 line-clamp-2 leading-snug"
        >{{@label}}</Text>
      {{/if}}
    {{else}}
      {{! Default layout: icon-badge + label row }}
      <div class="tio-row mb-3 min-w-0">
        {{#if @icon}}
          {{#let (if @color @color "gray") as |c|}}
            <div class="app-icon-badge app-icon-badge--{{c}} app-icon-badge--md shrink-0">
              {{#let @icon as |Icon|}}
                <Icon @size={{24}} />
              {{/let}}
            </div>
          {{/let}}
        {{/if}}
        <Text
          data-test-stat-card-label
          @variant="muted"
          class="app-stat-card-label line-clamp-2 leading-snug min-w-0"
        >{{@label}}</Text>
      </div>
    {{/if}}

    <div
      data-test-stat-card-value
      class="app-stat-card-value {{valueColorClass @valueColor @valueClass}}"
    >
      {{@value}}{{if @suffix @suffix}}
    </div>

    {{#if @subtext}}
      <Text data-test-stat-card-subtext @variant="muted" class="text-xs mt-1">{{@subtext}}</Text>
    {{/if}}

    {{#if @trendText}}
      {{#if @compact}}
        <div class="tio-row-extra-tight mt-1">
          {{#if (eq @trend "up")}}
            <svg
              role="presentation"
              class="w-3.5 h-3.5 tio-text-success"
              fill="currentColor"
              viewBox="0 0 20 20"
            ><path
                role="presentation"
                fill-rule="evenodd"
                d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              /></svg>
          {{else if (eq @trend "down")}}
            <svg
              role="presentation"
              class="w-3.5 h-3.5 tio-text-error"
              fill="currentColor"
              viewBox="0 0 20 20"
            ><path
                role="presentation"
                fill-rule="evenodd"
                d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              /></svg>
          {{/if}}
          <Text
            data-test-stat-card-trend
            @size="sm"
            class={{trendColorClass @trend}}
          >{{@trendText}}</Text>
        </div>
      {{else}}
        <span
          data-test-stat-card-trend
          class="text-xs font-medium mt-1 inline-block {{trendColorClass @trend}}"
        >
          {{@trendText}}
        </span>
      {{/if}}
    {{else if @secondaryText}}
      <Text
        data-test-stat-card-secondary
        @size="sm"
        class="tio-text-muted"
      >{{@secondaryText}}</Text>
    {{/if}}

    {{#if (has-block "extra")}}
      <div class="mt-2">
        {{yield to="extra"}}
      </div>
    {{/if}}
  </div>
</template>;

export default StatCard;

export type { StatCardSignature };
